import React, { useEffect, useState } from "react";
import { useOutletContext, Link, useNavigate } from "react-router-dom";
import { ArrowRight, Home, CalendarClock, Sparkles, Gift, MessageCircle, Phone, FileText, Clock, CheckCircle2, Wallet, Star, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSite, whatsappLink } from "@/context/SiteContext";

const STATUS_TONE = {
  "Completed": "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  "Cancelled": "text-red-300 border-red-500/30 bg-red-500/10",
  "Scheduled": "text-gold-soft border-[color:var(--border-strong)] bg-gold/10",
  "In Progress": "text-cyan-300 border-cyan-500/30 bg-cyan-500/10",
};
const tone = (s) => STATUS_TONE[s] || "text-white/70 border-white/20 bg-white/5";

export default function AccountDashboard() {
  const { me } = useOutletContext();
  const site = useSite();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/client/dashboard").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submitReview = async () => {
    if (!d?.review_prompt) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/client/jobs/${d.review_prompt.job_id}/review`, { rating, comment: reviewText });
      if (data.points_awarded) toast.success(`Thank you! +${d.review_prompt.points} points added`);
      else toast.success("Thank you for your feedback");
      setReviewOpen(false); setReviewText("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit review"); }
    finally { setBusy(false); }
  };

  const first = (me?.first_name || "there").split(" ")[0];
  const up = d?.upcoming;
  const ns = d?.next_step;

  return (
    <div className="space-y-6" data-testid="account-dashboard">
      {/* Hero */}
      <div>
        <p className="kicker mb-2">My Ascendia</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white">Welcome back, {first}</h1>
        <p className="mt-2 text-secondary">Your properties. Your services. Your Ascendia experience.</p>
      </div>

      {/* NEXT STEP — the one thing to do now */}
      {ns && (
        <div className={`ascendia-card p-6 ${ns.action_required ? "border-[color:var(--border-strong)] bg-gold/[0.06]" : ""}`} data-testid="dashboard-next-step">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border ${ns.action_required ? "bg-gold/15 border-[color:var(--border-strong)]" : "bg-white/5 border-[color:var(--border-subtle)]"}`}>
                {ns.action_required ? <Sparkles className="h-4 w-4 text-gold-soft" /> : <CheckCircle2 className="h-4 w-4 text-gold-soft" />}
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] text-gold-soft">{ns.action_required ? "Action required" : "You're all set"}</p>
                <p className="text-white font-semibold mt-0.5">{ns.title}</p>
                <p className="text-sm text-muted-c mt-0.5">{ns.subtitle}{ns.ref ? ` · ${ns.ref}` : ""}</p>
              </div>
            </div>
            <button onClick={() => nav(ns.cta_to)} data-testid="dashboard-next-step-cta"
              className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold ${ns.action_required ? "bg-gold text-black hover:bg-gold-soft" : "border border-[color:var(--border-subtle)] text-white hover:bg-white/5"}`}>
              {ns.cta_label} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Review prompt — earn points after a completed service */}
      {d?.review_prompt && (
        <div className="ascendia-card p-6 border-[color:var(--border-strong)] bg-gold/[0.06]" data-testid="dashboard-review-prompt">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gold/15 border border-[color:var(--border-strong)]">
                <Star className="h-4 w-4 text-gold-soft" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] text-gold-soft">How did we do?</p>
                <p className="text-white font-semibold mt-0.5">Review your {d.review_prompt.service || "recent service"}</p>
                <p className="text-sm text-muted-c mt-0.5">
                  {d.review_prompt.ref ? `${d.review_prompt.ref} · ` : ""}
                  {d.review_prompt.points ? `Earn ${d.review_prompt.points} points for your feedback` : "Share your feedback"}
                </p>
              </div>
            </div>
            <button onClick={() => { setRating(5); setReviewText(""); setReviewOpen(true); }} data-testid="dashboard-review-cta"
              className="inline-flex items-center gap-2 rounded-xl bg-gold px-5 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft">
              Leave a review <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Reward credit teaser — a ready-to-use balance toward the next service */}
      {d?.reward_credit > 0 && (
        <div className="ascendia-card card-accent-top p-6" data-testid="dashboard-credit-teaser">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30">
                <Wallet className="h-4 w-4 text-emerald-300" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] text-emerald-300">Ready to use</p>
                <p className="text-white font-semibold mt-0.5">{"\u20aa"}{Number(d.reward_credit).toLocaleString()} Ascendia credit</p>
                <p className="text-sm text-muted-c mt-0.5">We'll apply this automatically to your next quote.</p>
              </div>
            </div>
            <Link to="/account/quotes" data-testid="dashboard-credit-cta" className="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border-subtle)] px-4 py-2.5 text-sm text-white hover:bg-white/5">
              View quotes <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Upcoming service */}
      <div className="ascendia-card card-accent-top p-6" data-testid="dashboard-upcoming">
        <div className="flex items-center gap-2 mb-4"><CalendarClock className="h-5 w-5 text-gold-soft" /><h2 className="font-display text-lg font-semibold text-white">Upcoming service</h2></div>
        {up ? (
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-white font-semibold">{up.service || "Service"}</p>
              <p className="text-sm text-muted-c mt-1">{[up.city, up.date, up.slot].filter(Boolean).join(" · ") || "Date to be confirmed"}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${tone(up.status)}`}>{up.status}</span>
                {up.payment && <span className="text-[11px] px-2 py-0.5 rounded-full border border-white/20 text-white/70"><Wallet className="inline h-3 w-3 mr-1" />{up.payment.state}</span>}
              </div>
            </div>
            <button onClick={() => nav("/account/bookings")} data-testid="dashboard-view-booking" className="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border-subtle)] px-4 py-2.5 text-sm text-white hover:bg-white/5">View booking <ArrowRight className="h-4 w-4" /></button>
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-sm text-muted-c">No upcoming service yet.</p>
            <Link to="/property-intelligence" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gold px-5 py-2.5 text-sm font-semibold text-black hover:bg-gold-soft">Start a property assessment <ArrowRight className="h-4 w-4" /></Link>
          </div>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-4">
        {[["Properties", d?.properties_count ?? 0, Home, "/account/properties"], ["Quotes", d?.quotes_count ?? 0, FileText, "/account/quotes"], ["Bookings", d?.jobs_count ?? 0, CalendarClock, "/account/bookings"]].map(([label, val, Icon, to]) => (
          <Link key={label} to={to} className="ascendia-card p-4 sm:p-5 hover:border-[color:var(--border-strong)] transition-colors">
            <Icon className="h-4 w-4 text-gold-soft" />
            <p className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-white">{val}</p>
            <p className="text-xs text-muted-c">{label}</p>
          </Link>
        ))}
      </div>

      {/* Recent activity */}
      <div className="ascendia-card p-6" data-testid="dashboard-activity">
        <div className="flex items-center gap-2 mb-4"><Clock className="h-5 w-5 text-gold-soft" /><h2 className="font-display text-lg font-semibold text-white">Recent activity</h2></div>
        {d?.activity?.length ? (
          <ol className="space-y-3">
            {d.activity.map((a, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gold/10 border border-[color:var(--border-subtle)]">{a.kind === "report" ? <CheckCircle2 className="h-3.5 w-3.5 text-gold-soft" /> : a.kind === "quote" ? <FileText className="h-3.5 w-3.5 text-gold-soft" /> : <Sparkles className="h-3.5 w-3.5 text-gold-soft" />}</span>
                <div><p className="text-sm text-white">{a.label}</p><p className="text-xs text-muted-c">{(a.date || "").slice(0, 10)}</p></div>
              </li>
            ))}
          </ol>
        ) : <p className="text-sm text-muted-c">No activity yet. It will appear here as you request quotes and services.</p>}
      </div>

      {/* Membership + Rewards placeholders */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="ascendia-card card-accent-top p-6" data-testid="dashboard-membership">
          <div className="flex items-center gap-2 mb-2"><Sparkles className="h-5 w-5 text-gold-soft" /><h3 className="font-display font-semibold text-white">Ascendia Membership</h3></div>
          <p className="text-sm"><span className="text-gold-soft font-medium">{d?.membership?.tier || "Standard"} Member</span></p>
          <p className="mt-2 text-sm text-muted-c">{d?.membership_settings?.coming_soon_message || "Your Ascendia membership is being built."}</p>
        </div>
        <div className="ascendia-card p-6" data-testid="dashboard-rewards">
          <div className="flex items-center gap-2 mb-2"><Gift className="h-5 w-5 text-gold-soft" /><h3 className="font-display font-semibold text-white">Ascendia Rewards</h3></div>
          {d?.membership_settings?.rewards_enabled ? (
            <div className="mt-1">
              <p className="font-display text-3xl font-semibold text-white">{(d?.membership?.points ?? 0).toLocaleString()}</p>
              <p className="text-xs text-muted-c">points available · {(d?.membership?.lifetime_points ?? 0).toLocaleString()} lifetime</p>
              {d?.rewards_teaser?.next_reward ? (
                <div className="mt-3" data-testid="dashboard-next-reward">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-secondary">Next: {d.rewards_teaser.next_reward.title}</span>
                    <span className="text-gold-soft">{d.rewards_teaser.next_reward.points_to_go} pts to go</span>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full bg-gold transition-[width] duration-500"
                      style={{ width: `${Math.min(100, Math.round(((d?.membership?.points ?? 0) / (d.rewards_teaser.next_reward.points_cost || 1)) * 100))}%` }} />
                  </div>
                </div>
              ) : d?.rewards_teaser?.affordable_count > 0 ? (
                <p className="mt-3 text-xs text-emerald-300" data-testid="dashboard-rewards-ready">{d.rewards_teaser.affordable_count} reward{d.rewards_teaser.affordable_count > 1 ? "s" : ""} ready to claim</p>
              ) : null}
              <Link to="/account/rewards" className="mt-3 inline-flex items-center gap-1 text-sm text-gold-soft hover:text-gold">View rewards <ArrowRight className="h-4 w-4" /></Link>
            </div>
          ) : (
            <div className="mt-2 space-y-1 text-sm text-muted-c">
              <p>Points balance: <span className="text-white">—</span></p>
              <p>Tier: <span className="text-white">{d?.membership?.tier || "Standard"}</span></p>
              <p>Rewards available: <span className="text-gold-soft">Coming Soon</span></p>
            </div>
          )}
        </div>
      </div>

      {/* Contact hub */}
      <div className="ascendia-card p-6">
        <h3 className="font-display font-semibold text-white mb-4">Contact Ascendia</h3>
        <div className="grid grid-cols-3 gap-3">
          <a href={whatsappLink(site)} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1.5 rounded-xl border border-[color:var(--border-subtle)] py-4 text-xs text-white hover:bg-white/5"><MessageCircle className="h-5 w-5 text-[#25D366]" /> WhatsApp</a>
          <a href={`tel:${site.phone}`} className="flex flex-col items-center gap-1.5 rounded-xl border border-[color:var(--border-subtle)] py-4 text-xs text-white hover:bg-white/5"><Phone className="h-5 w-5 text-gold-soft" /> Call</a>
          <Link to="/ask" className="flex flex-col items-center gap-1.5 rounded-xl border border-[color:var(--border-subtle)] py-4 text-xs text-white hover:bg-white/5"><Sparkles className="h-5 w-5 text-gold-soft" /> Ask</Link>
        </div>
      </div>
      {/* Review modal */}
      {reviewOpen && d?.review_prompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => !busy && setReviewOpen(false)}>
          <div className="w-full max-w-md ascendia-card p-6 bg-[#101011]" onClick={(e) => e.stopPropagation()} data-testid="review-modal">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-lg font-semibold text-white">Review your service</h3>
                <p className="mt-1 text-sm text-muted-c">{d.review_prompt.service}{d.review_prompt.ref ? ` · ${d.review_prompt.ref}` : ""}</p>
              </div>
              <button onClick={() => setReviewOpen(false)} className="text-white/50 hover:text-white" data-testid="review-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 flex items-center gap-1.5" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setRating(n)} data-testid={`review-star-${n}`} className="p-1 transition-transform hover:scale-110" aria-label={`${n} star${n > 1 ? "s" : ""}`}>
                  <Star className={`h-7 w-7 ${n <= rating ? "fill-gold text-gold" : "text-white/25"}`} />
                </button>
              ))}
            </div>
            <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} rows={4} data-testid="review-comment"
              placeholder="Tell us about your experience (optional)…"
              className="mt-4 w-full rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]" />
            {d.review_prompt.points > 0 && (
              <p className="mt-2 text-xs text-gold-soft">You'll earn {d.review_prompt.points} Ascendia points for your feedback.</p>
            )}
            <div className="mt-4 flex gap-3">
              <button onClick={() => setReviewOpen(false)} disabled={busy} className="flex-1 rounded-xl border border-[color:var(--border-subtle)] py-2.5 text-sm text-white/70 hover:bg-white/5 disabled:opacity-50">Cancel</button>
              <button onClick={submitReview} disabled={busy} data-testid="review-submit"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gold py-2.5 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />} Submit review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
